import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';

interface AuthState {
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  user?: string;
}

/**
 * Is this token still in date?
 *
 * The panel used to treat the mere PRESENCE of the cookie as proof of a session,
 * so a token that expired overnight still unlocked the whole app: the dashboard
 * painted in full, every request behind it 401'd, and `AuthLayout` bounced the
 * operator away from /login because it read the same flag — leaving no way back
 * to the login screen short of clearing cookies by hand.
 *
 * Reading `exp` costs nothing and settles the common case locally. It is not a
 * security check — the signature is only ever verified by the server, and a
 * token this says is live can still be refused (wrong secret, signed out
 * elsewhere). That path is handled where it belongs, on the 401.
 */
const isTokenLive = (token: string): boolean => {
  const payload = token.split('.')[1];
  if (!payload) return false;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
    // A token with no `exp` has nothing to measure, so leave it to the server.
    if (typeof claims.exp !== 'number') return true;
    return claims.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

// 👇 Get initial token from cookies — and drop it if it has already lapsed, so
// boot-up lands on the login page instead of an empty, un-leavable dashboard.
const getInitialToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  const token = Cookies.get('authToken');
  if (!token) return null;
  if (isTokenLive(token)) return token;
  Cookies.remove('authToken');
  return null;
};

const initialToken = getInitialToken();

const initialState: AuthState = {
  accessToken: initialToken,
  isAuthenticated: Boolean(initialToken),
  isLoading: false,
  error: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // ✅ Set token after successful login
    setToken: (state, action: PayloadAction<string>) => {
      const token = action.payload;
      state.accessToken = token;
      state.isAuthenticated = true;
      state.error = null;
      state.isLoading = false;

      // Save in cookie (1 day expiry)
      Cookies.set('authToken', token, {
        expires: 1,
        secure: true,
        sameSite: 'strict',
      });
    },

    // ✅ Remove token on logout
    logout: (state) => {
      state.accessToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;

      // Remove from cookie
      Cookies.remove('authToken');
    },
  },
});

export const { setLoading, setError, setToken, logout } = authSlice.actions;
export default authSlice.reducer;
